import React from 'react';
import MetaFormInline from 'Components/UI/Meta/MetaForm/Components/MetaFormInline';
import {
  setDisabled,
  setSteps,
  useFormStepCollectionContext,
} from 'Components/UI/Accounts/AccountApplication/AccountApplicationContext';
import PersonalInformation from 'Components/UI/Accounts/AccountApplication/Step/PersonalInformation';
import { AccountApplicationStepOverride } from 'Overrides/AccountDetails';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import StepActions from 'Components/UI/Accounts/AccountApplication/StepActions';
import { displayErrorMessage } from 'Helpers/FacetNotificationManager';
import { Divider } from '@material-ui/core';
import { Fade } from '@mui/material';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import EmploymentInformation from 'Components/UI/Accounts/AccountApplication/Step/EmploymentAndOtherInformation';
import TrustedContactInformation from 'Components/UI/Accounts/AccountApplication/Step/TrustedContactInformation';
import fr from 'facet-rest';
import closeModal, { openModal } from 'Helpers/ModalHelper';
import { buildModalQuery } from 'Consts/ModalQueries';
import MODAL_NAMES from 'Consts/ModalNames';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router';
import useEventTrackingLogEvent from 'EventTracking/useEventTrackingLogEvent';
import BeneficiariesInformation from 'Components/UI/Accounts/AccountApplication/Step/BeneficiaryInformation';
import { BlockingDialog } from 'Components/UI/ConfirmationDialog';
import { pickFunction, pickWithPriority } from 'Helpers/Affordances';
import { dispatchManagedAccountsNavigationListRefresh } from 'Hooks/useRefreshEvent';
import ContactInformationWithMailingAddress
  from 'Components/UI/Accounts/AccountApplication/Step/ContactInformationWithMailingAddress';
import DecedentInformation from 'Components/UI/Accounts/AccountApplication/Step/DecendentInformation';

const APEX_ACCOUNT_DETAILS_TRUSTED_CONTACTINFORMATION_STEP = 'ApexAccountDetailsTrustedContactInformationStep';
const APEX_ACCOUNT_DETAILS_DECEDENT_INFORMATION_STEP = 'ApexAccountDetailsDecedentInformationStep';
const APEX_ACCOUNT_DETAILS_EMPLOYMENT_INFORMATION_STEP = 'ApexAccountDetailsEmploymentInformationStep';
const APEX_ACCOUNT_DETAILS_CONTACT_INFORMATION_STEP = 'ApexAccountDetailsContactInformationStep';
const APEX_ACCOUNT_DETAILS_BENEFICIARY_INFORMATION_STEP = 'ApexAccountDetailsBeneficiaryInformationStep';
const APEX_ACCOUNT_DETAILS_PERSONAL_INFORMATION_STEP = 'ApexAccountDetailsPersonalInformationStep';

const Form = withStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column-reverse',
    maxWidth: theme.dimensions.xl,
    margin: 'auto',
  },
}))(MetaFormInline);

const StepLayout = withStyles(theme => ({
  root: {
    maxWidth: theme.dimensions.xl,
    margin: 'auto',
  },
  loadingIndicator: {
    marginLeft: theme.spacing(1),
  },
}))(({ classes, onApplicationSubmit, ...props }) => {
  const { disabled } = props;
  const {
    state: { activeStep, steps, editMode },
  } = useFormStepCollectionContext();
  const {
    rootNodeProps,
    logEvent,
  } = useEventTrackingLogEvent({
    nodeName: `Account Application - ${steps.resources().index(activeStep).attr('stepName')}`,
    isPageView: true,
  });

  const {
    resource, onSubmit, saving, handleChange: handleResourceChange,
  } = useMetaLayoutContext();
  const history = useHistory();

  const navigateToStep = (nextStep) => {
    const search = new URLSearchParams(history.location.search);
    search.set('activeStep', nextStep);
    history.push({
      pathname: history.location.pathname,
      search: search.toString(),
    });
  };

  const handleChange = async nextStep => {
    const nextStepName = nextStep >= steps.resources().length() ? '' : steps.resources().index(nextStep).attr('stepName');

    // Go back
    if (nextStep < activeStep) {
      logEvent(`Account Application - Navigating back: ${nextStepName}`);
      return navigateToStep(nextStep);
    }

    // Save changes
    const resp = await onSubmit();
    if (resp && !(resp instanceof Error)) {
      // If we're on the final step, submit the entire application
      if (nextStep === steps.resources().length()) {
        logEvent('Account Application - Submitting');
        const appResp = await onApplicationSubmit();
        if (appResp) {
          const [details] = appResp;
          dispatchManagedAccountsNavigationListRefresh();
          closeModal();
          const modalQueryParams = pickFunction(details, {
            [fr.relationships.fundingInstructions.name]: () => ({
              accountCreationRequestLink: details
                .relationship(
                  fr.relationships.accountCreationRequestNoCache.name,
                )
                .link('related')
                .url(),
              fundingInstructionsLink: details
                .relationship(
                  fr.relationships.fundingInstructions.name,
                )
                .link('related')
                .url(),
            }),
            [fr.relationships.default.name]: () => ({
              accountCreationRequestLink: details
                .relationship(
                  fr.relationships.accountCreationRequestNoCache.name,
                )
                .link('related')
                .url(),
            }),
          });
          return openModal(
            buildModalQuery(MODAL_NAMES.postAccountCreation, modalQueryParams),
          );
        }
      } else {
        logEvent(`Account Application - Navigating next: ${nextStepName}`);
        return navigateToStep(nextStep);
      }
    }

    logEvent(`Account Application - Failed to navigate on step: ${steps.resources().index(activeStep).attr('stepName')}`);
    return null;
  };

  let Step = null;
  switch (resource?.type()) {
    case APEX_ACCOUNT_DETAILS_PERSONAL_INFORMATION_STEP: {
      Step = <PersonalInformation {...props} />;
      break;
    }
    case APEX_ACCOUNT_DETAILS_CONTACT_INFORMATION_STEP: {
      Step = pickWithPriority(resource, [
      ], <ContactInformationWithMailingAddress {...props} />);
      break;
    }
    case APEX_ACCOUNT_DETAILS_DECEDENT_INFORMATION_STEP: {
      Step = <DecedentInformation {...props} />;
      break;
    }
    case APEX_ACCOUNT_DETAILS_EMPLOYMENT_INFORMATION_STEP: {
      Step = <EmploymentInformation {...props} />;
      break;
    }
    case APEX_ACCOUNT_DETAILS_TRUSTED_CONTACTINFORMATION_STEP: {
      Step = <TrustedContactInformation {...props} />;
      break;
    }
    case APEX_ACCOUNT_DETAILS_BENEFICIARY_INFORMATION_STEP: {
      Step = <BeneficiariesInformation {...props} />;
      break;
    }
    default: {
      return null;
    }
  }
  const handleSave = async () => {
    const resp = await onSubmit();
    if (!resp || resp.status() !== 200) throw new Error('Failed to submit.');
  };

  return (
    <div className={classes.root} {...rootNodeProps}>
      <BlockingDialog
        test="confirm-account-application-changes"
        disabled={disabled}
        onConfirm={handleSave}
        resource={steps.resources().index(activeStep)}
        onProceedWithoutSaving={() => {
          for (const [key, val] of resource.dirtyAttributes()) {
            handleResourceChange(key, val);
          }
        }}
      />
      <Fade in>
        <Box>{Step}</Box>
      </Fade>
      <Divider />
      <StepActions
        totalSteps={steps?.resources().length()}
        activeStep={activeStep}
        onChange={handleChange}
        disabledNext={resource.attr('updateNotAllowed') === 'true' || disabled || saving || !resource.isValid()}
        disabledBack={disabled || saving}
        editMode={editMode}
        loading={saving || disabled}
        nextButtonText={
                    steps?.resources().length() - 1 === activeStep ? 'Submit' : 'Next'
                }
        isFinalStep={steps?.resources().length() - 1 === activeStep}
      />
    </div>
  );
});

function AccountApplicationStepSelector({ classes, ...props }) {
  const {
    state: {
      disabled,
      steps,
      activeStep,
      lastStepIdx = steps ? steps.resources().length() - 1 : 0,
    },
    dispatch,
  } = useFormStepCollectionContext();

  const [errors, setErrors] = React.useState(null);

  const step = lastStepIdx < activeStep ? null : steps?.resources().index(activeStep);
  if (!step) {
    return null;
  }

  return (
    <Form
      {...props}
      key={step.id()}
      noIndividualSubmitButtons
      resource={step}
      onChange={errors ? () => setErrors(null) : null}
      layout={StepLayout}
      LayoutProps={{
        disabled,
        errors,
        onApplicationSubmit: async () => {
          try {
            dispatch(setDisabled(true));
            const [details] = await steps
              .resources()
              .last()
              .relationship(fr.relationships.accountDetails.name)
              .link('related')
              .get();
            const appResp = await details.link('self').post();
            return appResp;
          } catch (e) {
            if (e.errors) {
              setErrors(e.errors);
            } else {
              setErrors([{
                title: 'Unknown error',
                detail: 'An unexpected error occurred while submitting your application.',
              }]);
            }
            dispatch(setDisabled(false));
            return null;
          } finally {
            dispatch(setDisabled(false));
          }
        },
      }}
      onSubmit={async resp => {
        try {
          dispatch(setDisabled(true));
          const nextSteps = await steps.refresh();
          dispatch(setSteps(nextSteps));
          dispatch(setDisabled(false));
          return resp;
        } catch (e) {
          dispatch(setDisabled(false));
          displayErrorMessage(e, 'Failed refresh account application');
          return resp;
        }
      }}
      metaOverrideSet={AccountApplicationStepOverride}
    />
  );
}

AccountApplicationStepSelector.propTypes = {
  onError: PropTypes.func.isRequired,
};

export default AccountApplicationStepSelector;
